// Pure metric functions computed from data already in the app (weekly indicators,
// events, news counts, price series). No fetching, no side effects — every function
// takes plain arrays and returns a plain object, so they unit-test trivially and the
// UI layer stays dumb.
//
// Design constraints (from the metrics-health plan + red-team):
// - Weekly data is SPARSE (1-5 events/week, a handful of weeks). Metrics compare the
//   latest available week vs the prior available week — never assume N contiguous weeks.
// - Empty / single-point input returns a valid empty-ish object, never NaN.
// - Stats reuse mean/std from trend-extrapolation (DRY — no duplicate implementations).

import type { WeeklyIndicator } from "../services/api-client.ts";
import { DOMAINS, type Domain } from "../../functions/_shared/event-taxonomy.ts";
import { mean } from "./trend-extrapolation.ts";

// ---------------------------------------------------------------------------
// Domain Pulse — per-domain activity, latest available week vs the prior one.
// ---------------------------------------------------------------------------

export type PulseDirection = "up" | "down" | "flat";

export interface DomainPulse {
  domain: Domain;
  latest: number; // event_count in the latest week that has any data
  prior: number; // event_count in the previous week with data (0 if none)
  deltaPct: number | null; // (latest-prior)/prior*100; null when prior is 0
  direction: PulseDirection;
  latestWeek: string | null; // ISO 'YYYY-Www' of the latest week, null if no data
}

// ISO week labels sort lexicographically ('2026-W07' < '2026-W28'), so plain string
// comparison gives chronological order — no date parsing needed.
function sortedWeeksDesc(rows: WeeklyIndicator[]): string[] {
  return [...new Set(rows.map((r) => r.week))].sort().reverse();
}

// Per domain: compare the two most recent weeks that actually have rows for THAT
// domain (weeks with no row for a domain are skipped, not treated as zero — a missing
// week means "not yet aggregated", which is different from "zero events").
export function domainPulse(weekly: WeeklyIndicator[]): DomainPulse[] {
  const out: DomainPulse[] = [];
  for (const domain of DOMAINS) {
    const rows = weekly
      .filter((r) => r.domain === domain)
      .sort((a, b) => (a.week < b.week ? 1 : a.week > b.week ? -1 : 0)); // newest first
    if (rows.length === 0) {
      out.push({ domain, latest: 0, prior: 0, deltaPct: null, direction: "flat", latestWeek: null });
      continue;
    }
    const latest = rows[0].event_count;
    const prior = rows.length > 1 ? rows[1].event_count : 0;
    let deltaPct: number | null = null;
    let direction: PulseDirection = "flat";
    if (rows.length > 1 && prior > 0) {
      deltaPct = ((latest - prior) / prior) * 100;
      direction = latest > prior ? "up" : latest < prior ? "down" : "flat";
    } else if (rows.length > 1) {
      // prior week existed with 0 events; any activity now is "up".
      direction = latest > 0 ? "up" : "flat";
    }
    out.push({ domain, latest, prior, deltaPct, direction, latestWeek: rows[0].week });
  }
  return out;
}

// ---------------------------------------------------------------------------
// News Velocity — is news flow accelerating vs its recent baseline?
// ---------------------------------------------------------------------------

export interface NewsVelocity {
  avg7: number; // mean daily count over last 7 days
  avg30: number; // mean daily count over last 30 days
  ratio: number | null; // avg7 / avg30; null when avg30 is 0
  state: "accelerating" | "slowing" | "steady" | "no-data";
}

// counts7 / counts30 are per-day news counts (most-recent-first or any order — we
// only average them). ratio > 1.3 => accelerating, < 0.7 => slowing.
export function newsVelocity(counts7: number[], counts30: number[]): NewsVelocity {
  const avg7 = mean(counts7);
  const avg30 = mean(counts30);
  if (counts30.length === 0 || avg30 === 0) {
    return { avg7, avg30, ratio: null, state: "no-data" };
  }
  const ratio = avg7 / avg30;
  const state = ratio > 1.3 ? "accelerating" : ratio < 0.7 ? "slowing" : "steady";
  return { avg7, avg30, ratio, state };
}

// ---------------------------------------------------------------------------
// Policy Activity — count of policy-issuance events per week (from weekly rows).
// ---------------------------------------------------------------------------

export interface PolicyActivityWeek {
  week: string;
  count: number;
  cumulative: number;
}

// Uses the 'policy' domain rows' event_count as the weekly policy-activity signal.
// Returns chronological (oldest-first) so a bar+cumulative chart can render directly.
export function policyActivity(weekly: WeeklyIndicator[]): PolicyActivityWeek[] {
  const weeks = sortedWeeksDesc(weekly).reverse(); // oldest first
  const byWeek = new Map<string, number>();
  for (const r of weekly) {
    if (r.domain === "policy") byWeek.set(r.week, (byWeek.get(r.week) ?? 0) + r.event_count);
  }
  let cumulative = 0;
  const out: PolicyActivityWeek[] = [];
  for (const week of weeks) {
    const count = byWeek.get(week) ?? 0;
    cumulative += count;
    out.push({ week, count, cumulative });
  }
  return out;
}

// ---------------------------------------------------------------------------
// Top Movers — provinces with the largest week-over-week change in event_count.
// ---------------------------------------------------------------------------

export interface TopMover {
  provinceCode: string;
  latest: number;
  prior: number;
  delta: number; // latest - prior (signed)
}

// Per province, compare the two most recent weeks with rows for that province.
// Returns the top `limit` by absolute delta. Provinces with <2 weeks are skipped
// (no basis for a WoW change). Empty input -> [].
export function topMovers(weekly: WeeklyIndicator[], limit = 5): TopMover[] {
  const byProvince = new Map<string, WeeklyIndicator[]>();
  for (const r of weekly) {
    if (!r.province_code) continue; // skip the national aggregate ('' province)
    const list = byProvince.get(r.province_code) ?? [];
    list.push(r);
    byProvince.set(r.province_code, list);
  }
  const movers: TopMover[] = [];
  for (const [provinceCode, rows] of byProvince) {
    rows.sort((a, b) => (a.week < b.week ? 1 : a.week > b.week ? -1 : 0)); // newest first
    if (rows.length < 2) continue;
    const latest = rows[0].event_count;
    const prior = rows[1].event_count;
    movers.push({ provinceCode, latest, prior, delta: latest - prior });
  }
  movers.sort((a, b) => Math.abs(b.delta) - Math.abs(a.delta));
  return movers.slice(0, limit);
}
